/**
 * Marketplace Product Form Page (Create/Edit)
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Typography,
  Divider,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Image as ImageIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { PageHeader, ImageSizeHint } from '../../components/common';
import { IMAGE_SIZES } from '../../constants/imageSizes';
import {
  useGetMarketplaceProductQuery,
  useCreateMarketplaceProductMutation,
  useUpdateMarketplaceProductMutation,
  useGetMarketplaceCategoriesQuery,
  useGetMarketplaceSubCategoriesQuery,
  useUploadProductImageMutation,
  useDeleteProductImageMutation,
} from '../../store/api';
import { PricingType, StockStatus, ProductSpecification } from '../../types/marketplace';
import toast from 'react-hot-toast';

interface SpecRow {
  name: string;
  name_ar: string;
  value: string;
  value_ar: string;
}

interface ProductFormState {
  category: number | '';
  subcategory: number | '';
  name: string;
  name_ar: string;
  description: string;
  description_ar: string;
  pricing_type: PricingType;
  buy_price: string;
  compare_at_price: string;
  daily_rental_price: string;
  weekly_rental_price: string;
  min_rental_days: number;
  insurance_available: boolean;
  insurance_price: string;
  insurance_description: string;
  stock_status: StockStatus;
  sale_stock_quantity: number;
  rental_stock_quantity: number;
  warranty: string;
  warranty_ar: string;
  is_active: boolean;
  is_featured: boolean;
  order: number;
}

const defaultState: ProductFormState = {
  category: '',
  subcategory: '',
  name: '',
  name_ar: '',
  description: '',
  description_ar: '',
  pricing_type: 'BUY_ONLY',
  buy_price: '',
  compare_at_price: '',
  daily_rental_price: '',
  weekly_rental_price: '',
  min_rental_days: 1,
  insurance_available: false,
  insurance_price: '',
  insurance_description: '',
  stock_status: 'IN_STOCK',
  sale_stock_quantity: 0,
  rental_stock_quantity: 0,
  warranty: '',
  warranty_ar: '',
  is_active: true,
  is_featured: false,
  order: 0,
};

export const ProductFormPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { data: productData, isLoading: isLoadingProduct } = useGetMarketplaceProductQuery(
    Number(id!),
    { skip: !isEdit }
  );
  const { data: categoriesData } = useGetMarketplaceCategoriesQuery({ page_size: 100 });

  const [createProduct, { isLoading: isCreating }] = useCreateMarketplaceProductMutation();
  const [updateProduct, { isLoading: isUpdating }] = useUpdateMarketplaceProductMutation();
  const [uploadImage, { isLoading: isUploadingGallery }] = useUploadProductImageMutation();
  const [deleteImage] = useDeleteProductImageMutation();

  const [formData, setFormData] = useState<ProductFormState>(defaultState);
  const [specs, setSpecs] = useState<SpecRow[]>([]);
  const [mainImageFile, setMainImageFile] = useState<File | null>(null);
  const [mainImagePreview, setMainImagePreview] = useState<string | null>(null);
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);

  const { data: subcategoriesData } = useGetMarketplaceSubCategoriesQuery({
    page_size: 100,
    category: formData.category ? Number(formData.category) : undefined,
  });

  useEffect(() => {
    if (isEdit && productData?.data) {
      const p = productData.data;
      setFormData({
        category: p.category_id ?? '',
        subcategory: p.subcategory,
        name: p.name,
        name_ar: p.name_ar || '',
        description: p.description || '',
        description_ar: p.description_ar || '',
        pricing_type: p.pricing_type,
        buy_price: p.buy_price ?? '',
        compare_at_price: p.compare_at_price ?? '',
        daily_rental_price: p.daily_rental_price ?? '',
        weekly_rental_price: p.weekly_rental_price ?? '',
        min_rental_days: p.min_rental_days ?? 1,
        insurance_available: p.insurance_available,
        insurance_price: p.insurance_price ?? '',
        insurance_description: p.insurance_description || '',
        stock_status: p.stock_status,
        sale_stock_quantity: p.sale_stock_quantity ?? 0,
        rental_stock_quantity: p.rental_stock_quantity ?? 0,
        warranty: p.warranty || '',
        warranty_ar: p.warranty_ar || '',
        is_active: p.is_active,
        is_featured: p.is_featured,
        order: p.order ?? 0,
      });
      setSpecs(
        (p.specifications || []).map((s) => ({
          name: s.name || '',
          name_ar: s.name_ar || '',
          value: s.value || '',
          value_ar: s.value_ar || '',
        }))
      );
      setMainImagePreview(p.main_image || null);
    }
  }, [isEdit, productData]);

  const isSaving = isCreating || isUpdating;
  const isBuyable = formData.pricing_type === 'BUY_ONLY' || formData.pricing_type === 'RENT_AND_BUY';
  const isRentable = formData.pricing_type === 'RENT_ONLY' || formData.pricing_type === 'RENT_AND_BUY';

  const handleChange = (
    field: keyof ProductFormState,
    value: string | number | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setMainImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setMainImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSpecChange = (index: number, field: keyof SpecRow, value: string) => {
    setSpecs((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const addSpecRow = () => setSpecs((prev) => [...prev, { name: '', name_ar: '', value: '', value_ar: '' }]);
  const removeSpecRow = (index: number) => setSpecs((prev) => prev.filter((_, i) => i !== index));

  const handleGalleryUpload = async () => {
    if (!galleryFiles.length || !id) return;
    try {
      // Upload each selected gallery image sequentially.
      for (const file of galleryFiles) {
        const body = new FormData();
        body.append('image', file);
        await uploadImage({ productId: Number(id), data: body }).unwrap();
      }
      toast.success(
        galleryFiles.length > 1
          ? t('marketplaceCatalog.products.toast.imagesUploaded', { count: galleryFiles.length })
          : t('marketplaceCatalog.products.toast.imageUploaded')
      );
      setGalleryFiles([]);
    } catch (err: any) {
      toast.error(err?.data?.message || t('marketplaceCatalog.products.toast.uploadImagesError'));
    }
  };

  const handleGalleryDelete = async (imageId: number) => {
    if (!id) return;
    try {
      await deleteImage({ productId: Number(id), imageId }).unwrap();
      toast.success(t('marketplaceCatalog.products.toast.imageDeleted'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('marketplaceCatalog.products.toast.deleteImageError'));
    }
  };

  const buildSpecifications = (): ProductSpecification[] =>
    specs
      .filter((s) => s.name.trim() && s.value.trim())
      .map((s, index) => ({
        name: s.name.trim(),
        value: s.value.trim(),
        ...(s.name_ar.trim() ? { name_ar: s.name_ar.trim() } : {}),
        ...(s.value_ar.trim() ? { value_ar: s.value_ar.trim() } : {}),
        order: index,
      }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.subcategory) {
      toast.error(t('marketplaceCatalog.products.selectSubcategoryError'));
      return;
    }
    try {
      const payload: Record<string, unknown> = {
        subcategory: Number(formData.subcategory),
        name: formData.name,
        name_ar: formData.name_ar,
        description: formData.description,
        description_ar: formData.description_ar,
        pricing_type: formData.pricing_type,
        min_rental_days: formData.min_rental_days,
        // Insurance is a rental concept — never send it for buy-only products.
        insurance_available: isRentable && formData.insurance_available,
        stock_status: formData.stock_status,
        // Each pool only applies to the acquisition modes the product supports.
        sale_stock_quantity: isBuyable ? formData.sale_stock_quantity : 0,
        rental_stock_quantity: isRentable ? formData.rental_stock_quantity : 0,
        warranty: formData.warranty,
        warranty_ar: formData.warranty_ar,
        is_active: formData.is_active,
        is_featured: formData.is_featured,
        order: formData.order,
        specifications: buildSpecifications(),
      };

      if (isBuyable) {
        payload.buy_price = formData.buy_price || '0';
        payload.compare_at_price = formData.compare_at_price || null;
      }
      if (isRentable) {
        payload.daily_rental_price = formData.daily_rental_price || '0';
        payload.weekly_rental_price = formData.weekly_rental_price || null;
      }
      if (isRentable && formData.insurance_available) {
        payload.insurance_price = formData.insurance_price || '0';
        payload.insurance_description = formData.insurance_description;
      }

      let productId: number;
      if (isEdit) {
        const res = await updateProduct({ id: Number(id!), data: payload }).unwrap();
        productId = res.data.id;
        toast.success(t('marketplaceCatalog.products.toast.updated'));
      } else {
        const res = await createProduct(payload).unwrap();
        productId = res.data.id;
        toast.success(t('marketplaceCatalog.products.toast.created'));
      }

      // Upload main image separately as FormData (no specifications included)
      if (mainImageFile && productId) {
        const imageForm = new FormData();
        imageForm.append('main_image', mainImageFile);
        await updateProduct({ id: productId, data: imageForm }).unwrap();
      }

      // Upload any pending gallery images (works for both create and edit).
      if (galleryFiles.length && productId) {
        for (const file of galleryFiles) {
          const body = new FormData();
          body.append('image', file);
          await uploadImage({ productId, data: body }).unwrap();
        }
        setGalleryFiles([]);
      }

      navigate(`/marketplace/products/${productId}`);
    } catch (err: any) {
      toast.error(
        err?.data?.message ||
          (isEdit
            ? t('marketplaceCatalog.products.toast.updateError')
            : t('marketplaceCatalog.products.toast.createError'))
      );
    }
  };

  if (isEdit && isLoadingProduct) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={isEdit ? t('marketplaceCatalog.products.editTitle') : t('marketplaceCatalog.products.createTitle')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.products'), path: '/marketplace/products' },
          { label: isEdit ? t('common.edit') : t('common.create') },
        ]}
      />

      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/marketplace/products')} sx={{ mb: 3 }}>
        {t('marketplaceCatalog.products.back')}
      </Button>

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Main Image */}
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {t('marketplaceCatalog.products.mainImage')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                  <Avatar src={mainImagePreview || undefined} variant="rounded" sx={{ width: 120, height: 120 }}>
                    <ImageIcon sx={{ fontSize: 60 }} />
                  </Avatar>
                  <Button variant="outlined" component="label" disabled={isSaving}>
                    {mainImagePreview ? t('marketplaceCatalog.form.changeImage') : t('marketplaceCatalog.form.uploadImage')}
                    <input type="file" hidden accept="image/*" onChange={handleMainImageChange} />
                  </Button>
                </Box>
                <ImageSizeHint size={IMAGE_SIZES.productMain} />
              </Box>

              {/* Category & Subcategory */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('marketplaceCatalog.form.category')}</InputLabel>
                  <Select
                    value={formData.category}
                    label={t('marketplaceCatalog.form.category')}
                    onChange={(e) => {
                      handleChange('category', Number(e.target.value));
                      handleChange('subcategory', '');
                    }}
                    disabled={isSaving}
                  >
                    {categoriesData?.results?.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth required>
                  <InputLabel>{t('marketplaceCatalog.form.subcategory')}</InputLabel>
                  <Select
                    value={formData.subcategory}
                    label={t('marketplaceCatalog.form.subcategory')}
                    onChange={(e) => handleChange('subcategory', Number(e.target.value))}
                    disabled={isSaving}
                  >
                    {subcategoriesData?.results?.map((subcategory) => (
                      <MenuItem key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <TextField
                fullWidth
                label={t('marketplaceCatalog.form.name')}
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                disabled={isSaving}
              />
              <TextField
                fullWidth
                label={t('marketplaceCatalog.form.nameAr')}
                value={formData.name_ar}
                onChange={(e) => handleChange('name_ar', e.target.value)}
                disabled={isSaving}
                inputProps={{ dir: 'rtl' }}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('marketplaceCatalog.form.description')}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                disabled={isSaving}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('marketplaceCatalog.form.descriptionAr')}
                value={formData.description_ar}
                onChange={(e) => handleChange('description_ar', e.target.value)}
                disabled={isSaving}
                inputProps={{ dir: 'rtl' }}
              />

              <Divider />

              {/* Pricing */}
              <Typography variant="subtitle1" fontWeight={600}>
                {t('marketplaceCatalog.products.sections.pricing')}
              </Typography>
              <FormControl fullWidth>
                <InputLabel>{t('marketplaceCatalog.form.pricingType')}</InputLabel>
                <Select
                  value={formData.pricing_type}
                  label={t('marketplaceCatalog.form.pricingType')}
                  onChange={(e) => handleChange('pricing_type', e.target.value as PricingType)}
                  disabled={isSaving}
                >
                  <MenuItem value="BUY_ONLY">{t('marketplaceCatalog.pricingType.BUY_ONLY')}</MenuItem>
                  <MenuItem value="RENT_ONLY">{t('marketplaceCatalog.pricingType.RENT_ONLY')}</MenuItem>
                  <MenuItem value="RENT_AND_BUY">{t('marketplaceCatalog.pricingType.RENT_AND_BUY')}</MenuItem>
                </Select>
              </FormControl>

              {isBuyable && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('marketplaceCatalog.form.buyPrice')}
                    value={formData.buy_price}
                    onChange={(e) => handleChange('buy_price', e.target.value)}
                    disabled={isSaving}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{ startAdornment: <InputAdornment position="start">EGP</InputAdornment> }}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label={t('marketplaceCatalog.form.compareAtPrice')}
                    value={formData.compare_at_price}
                    onChange={(e) => handleChange('compare_at_price', e.target.value)}
                    disabled={isSaving}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{ startAdornment: <InputAdornment position="start">EGP</InputAdornment> }}
                  />
                </Box>
              )}

              {isRentable && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('marketplaceCatalog.form.dailyRentalPrice')}
                    value={formData.daily_rental_price}
                    onChange={(e) => handleChange('daily_rental_price', e.target.value)}
                    disabled={isSaving}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{ startAdornment: <InputAdornment position="start">EGP</InputAdornment> }}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label={t('marketplaceCatalog.form.weeklyRentalPrice')}
                    value={formData.weekly_rental_price}
                    onChange={(e) => handleChange('weekly_rental_price', e.target.value)}
                    disabled={isSaving}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{ startAdornment: <InputAdornment position="start">EGP</InputAdornment> }}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label={t('marketplaceCatalog.form.minRentalDays')}
                    value={formData.min_rental_days}
                    onChange={(e) => handleChange('min_rental_days', parseInt(e.target.value) || 1)}
                    disabled={isSaving}
                    inputProps={{ min: 1 }}
                  />
                </Box>
              )}

              {/* Insurance — rental concept only; hidden for buy-only products */}
              {isRentable && (
                <>
                  <Divider />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.insurance_available}
                        onChange={(e) => handleChange('insurance_available', e.target.checked)}
                        disabled={isSaving}
                      />
                    }
                    label={t('marketplaceCatalog.form.insuranceAvailable')}
                  />
                  {formData.insurance_available && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('marketplaceCatalog.form.insurancePrice')}
                        value={formData.insurance_price}
                        onChange={(e) => handleChange('insurance_price', e.target.value)}
                        disabled={isSaving}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{ startAdornment: <InputAdornment position="start">EGP</InputAdornment> }}
                      />
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label={t('marketplaceCatalog.form.insuranceDescription')}
                        value={formData.insurance_description}
                        onChange={(e) => handleChange('insurance_description', e.target.value)}
                        disabled={isSaving}
                      />
                    </Box>
                  )}
                </>
              )}

              <Divider />

              {/* Stock */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('marketplaceCatalog.form.stockStatus')}</InputLabel>
                  <Select
                    value={formData.stock_status}
                    label={t('marketplaceCatalog.form.stockStatus')}
                    onChange={(e) => handleChange('stock_status', e.target.value as StockStatus)}
                    disabled={isSaving}
                  >
                    <MenuItem value="IN_STOCK">{t('marketplaceCatalog.stockStatus.IN_STOCK')}</MenuItem>
                    <MenuItem value="OUT_OF_STOCK">{t('marketplaceCatalog.stockStatus.OUT_OF_STOCK')}</MenuItem>
                    <MenuItem value="PREORDER">{t('marketplaceCatalog.stockStatus.PREORDER')}</MenuItem>
                  </Select>
                </FormControl>
                {isBuyable && (
                  <TextField
                    fullWidth
                    type="number"
                    label={t('marketplaceCatalog.form.saleStockQuantity')}
                    helperText={t('marketplaceCatalog.form.saleStockHelp')}
                    value={formData.sale_stock_quantity}
                    onChange={(e) => handleChange('sale_stock_quantity', parseInt(e.target.value) || 0)}
                    disabled={isSaving}
                    inputProps={{ min: 0 }}
                  />
                )}
                {isRentable && (
                  <TextField
                    fullWidth
                    type="number"
                    label={t('marketplaceCatalog.form.rentalStockQuantity')}
                    helperText={t('marketplaceCatalog.form.rentalStockHelp')}
                    value={formData.rental_stock_quantity}
                    onChange={(e) => handleChange('rental_stock_quantity', parseInt(e.target.value) || 0)}
                    disabled={isSaving}
                    inputProps={{ min: 0 }}
                  />
                )}
              </Box>

              {/* Warranty */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('marketplaceCatalog.form.warranty')}
                  value={formData.warranty}
                  onChange={(e) => handleChange('warranty', e.target.value)}
                  disabled={isSaving}
                />
                <TextField
                  fullWidth
                  label={t('marketplaceCatalog.form.warrantyAr')}
                  value={formData.warranty_ar}
                  onChange={(e) => handleChange('warranty_ar', e.target.value)}
                  disabled={isSaving}
                  inputProps={{ dir: 'rtl' }}
                />
              </Box>

              <Divider />

              {/* Specifications */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('marketplaceCatalog.products.sections.specifications')}
                  </Typography>
                  <Button startIcon={<AddIcon />} onClick={addSpecRow} size="small" variant="outlined" disabled={isSaving}>
                    {t('marketplaceCatalog.products.addSpecification')}
                  </Button>
                </Box>
                {specs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('marketplaceCatalog.products.noSpecs')}
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {specs.map((spec, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr) auto' },
                          gap: 1,
                          alignItems: 'center',
                        }}
                      >
                        <TextField
                          size="small"
                          label={t('marketplaceCatalog.form.name')}
                          value={spec.name}
                          onChange={(e) => handleSpecChange(index, 'name', e.target.value)}
                          disabled={isSaving}
                        />
                        <TextField
                          size="small"
                          label={t('marketplaceCatalog.cols.nameAr')}
                          value={spec.name_ar}
                          onChange={(e) => handleSpecChange(index, 'name_ar', e.target.value)}
                          disabled={isSaving}
                          inputProps={{ dir: 'rtl' }}
                        />
                        <TextField
                          size="small"
                          label={t('marketplaceCatalog.form.value')}
                          value={spec.value}
                          onChange={(e) => handleSpecChange(index, 'value', e.target.value)}
                          disabled={isSaving}
                        />
                        <TextField
                          size="small"
                          label={t('marketplaceCatalog.form.valueAr')}
                          value={spec.value_ar}
                          onChange={(e) => handleSpecChange(index, 'value_ar', e.target.value)}
                          disabled={isSaving}
                          inputProps={{ dir: 'rtl' }}
                        />
                        <IconButton color="error" onClick={() => removeSpecRow(index)} disabled={isSaving} title={t('marketplaceCatalog.buttons.remove')}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <Divider />

              {/* Flags */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch checked={formData.is_active} onChange={(e) => handleChange('is_active', e.target.checked)} disabled={isSaving} />
                  }
                  label={t('marketplaceCatalog.form.active')}
                />
                <FormControlLabel
                  control={
                    <Switch checked={formData.is_featured} onChange={(e) => handleChange('is_featured', e.target.checked)} disabled={isSaving} />
                  }
                  label={t('marketplaceCatalog.form.featured')}
                />
                <TextField
                  type="number"
                  label={t('marketplaceCatalog.form.order')}
                  value={formData.order}
                  onChange={(e) => handleChange('order', parseInt(e.target.value) || 0)}
                  disabled={isSaving}
                  inputProps={{ min: 0 }}
                  sx={{ width: 140 }}
                  InputProps={{ startAdornment: <InputAdornment position="start">#</InputAdornment> }}
                />
              </Box>

              {/* Gallery images */}
              <Divider />
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {t('marketplaceCatalog.products.galleryImages')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('marketplaceCatalog.products.galleryHint')}
                  {!isEdit && ' ' + t('marketplaceCatalog.products.galleryHintCreate')}
                </Typography>
                <ImageSizeHint size={IMAGE_SIZES.productGallery} sx={{ mt: 0, mb: 2 }} />

                {/* Existing gallery (edit mode) */}
                {isEdit && (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    {(productData?.data?.images || []).map((img) => (
                      <Box key={img.id} sx={{ position: 'relative' }}>
                        <Avatar src={img.image} variant="rounded" sx={{ width: 100, height: 100 }}>
                          <ImageIcon />
                        </Avatar>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleGalleryDelete(img.id)}
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                          title={t('marketplaceCatalog.products.deleteImageTitle')}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    {(productData?.data?.images || []).length === 0 && galleryFiles.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {t('marketplaceCatalog.products.noGalleryYet')}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Pending (selected, not yet uploaded) previews + the "+" add tile */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                  {galleryFiles.map((file, i) => (
                    <Box key={i} sx={{ position: 'relative' }}>
                      <Avatar src={URL.createObjectURL(file)} variant="rounded" sx={{ width: 100, height: 100, opacity: 0.7 }}>
                        <ImageIcon />
                      </Avatar>
                      <IconButton
                        size="small"
                        onClick={() => setGalleryFiles((prev) => prev.filter((_, idx) => idx !== i))}
                        sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                        title={t('marketplaceCatalog.products.removeFromSelection')}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}

                  {/* "+" tile — add one (or more) images; appends to the selection */}
                  <Box
                    component="label"
                    sx={{
                      width: 100,
                      height: 100,
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isSaving || isUploadingGallery ? 'not-allowed' : 'pointer',
                      color: 'text.secondary',
                      opacity: isSaving || isUploadingGallery ? 0.5 : 1,
                      '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                    }}
                    title={t('marketplaceCatalog.products.addImage')}
                  >
                    <AddIcon />
                    <Typography variant="caption">{t('marketplaceCatalog.products.addImage')}</Typography>
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      multiple
                      disabled={isSaving || isUploadingGallery}
                      onChange={(e) => {
                        const picked = Array.from(e.target.files || []);
                        if (picked.length) {
                          setGalleryFiles((prev) => [...prev, ...picked]);
                        }
                        // reset so picking the same file again still fires onChange
                        e.target.value = '';
                      }}
                    />
                  </Box>
                </Box>

                {/* Edit mode: upload the pending selection immediately without re-saving the form.
                    Create mode: the pending selection uploads automatically on submit. */}
                {isEdit && galleryFiles.length > 0 && (
                  <Button
                    variant="contained"
                    onClick={handleGalleryUpload}
                    disabled={isUploadingGallery}
                  >
                    {isUploadingGallery
                      ? t('marketplaceCatalog.products.uploading')
                      : t('marketplaceCatalog.products.uploadNow', { count: galleryFiles.length })}
                  </Button>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="outlined" onClick={() => navigate('/marketplace/products')} disabled={isSaving}>
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={isSaving || !formData.name || !formData.subcategory}
                >
                  {isSaving
                    ? t('marketplaceCatalog.form.saving')
                    : isEdit
                    ? t('marketplaceCatalog.products.updateButton')
                    : t('marketplaceCatalog.products.createButton')}
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProductFormPage;
